import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { TextButton, InfoWidget, Badge } from '@ds/react-components';
import { ReactComponent as CopyIcon } from 'assets/icons/ic-duplicate.svg';
import ClipboardJS from 'clipboard';
import { isNotEmptyArray } from 'utils/misc';
import useToggle from 'hooks/useToggle';
import SkeletonLoader from './SkeletonLoader';
import {
  RowLeftText,
  RowRightText,
  RowContainer,
} from '../common/InfoRowComponents';

import * as Styled from './PersonalInformation.styled';

const PersonalInformationPreview = (props) => {
  const { customerDetails, localisation, permissions } = props;

  const { dateFormat } = localisation.locales;

  const {
    adfitNo,
    businessId,
    name,
    fullName,
    dob,
    prefContact,
    contacts,
    prefAddress,
    addresses,
    firstName,
    isActive,
    cancelReason = 'Business request',
    customCancelReason,
    facilityName,
  } = customerDetails;

  const [expandContact, toggleExpandContact] = useToggle(false);
  const [expandAddress, toggleExpandAddress] = useToggle(false);
  const [clipButtonState, setClipButtonState] = useState({});

  const dobStr = dob && moment(dob).format(dateFormat);

  const { prompts } = localisation;

  useEffect(() => {
    const clipBoard = new ClipboardJS('button[data-clipboard-text]');
    clipBoard.on('success', (e) => {
      setClipButtonState((prevState) => ({
        ...prevState,
        [e.trigger.id]: 'Copied!',
      }));
    });
    return () => {
      clipBoard.destroy();
    };
  }, [setClipButtonState]);

  const preferredContact = useMemo(() => {
    if (!isNotEmptyArray(contacts)) return null;

    //Check if there is a preferred contact based on prefContact in customer table.
    if (prefContact) {
      const result = contacts.find((c) => c.contact === prefContact);
      if (result) return result;
    }

    //Check if any of the contacts are marked as preferred or else return the first one.
    return contacts.find((c) => c.preferred === true) || contacts[0];
  }, [contacts, prefContact]);

  const preferredAddress = useMemo(() => {
    if (!isNotEmptyArray(addresses)) return null;
    if (prefAddress) {
      const result = addresses.find((a) => a.addressId === prefAddress);
      if (result) return result;
    }
    return addresses.find((a) => a.preferred === true) || addresses[0];
  }, [addresses, prefAddress]);

  const filteredContacts = useMemo(() => {
    if (!isNotEmptyArray(contacts)) return [];
    return contacts.filter((c) => c.contactType !== 'PE');
  }, [contacts]);

  const filteredAddress = useMemo(() => {
    if (!isNotEmptyArray(addresses)) return [];
    return addresses.filter((c) => c.addressType !== 'PV');
  }, [addresses]);

  const previousAddress = useMemo(() => {
    if (!isNotEmptyArray(addresses)) return [];
    return addresses.filter((c) => c.addressType === 'PV');
  }, [addresses]);

  const getContactDisplayType = (contactType) => {
    if (
      !prompts ||
      !prompts.contactTypes ||
      !prompts.contactTypes[contactType]
    ) {
      return '';
    }
    return prompts.contactTypes[contactType] || '';
  };

  const renderContactByType = (contact) => {
    const { contactType, contactDetail } = contact;

    if (contactType === 'EM') {
      return <a href={`mailto:${contactDetail}`}>{contactDetail}</a>;
    }
    return <span>{contactDetail} </span>;
  };

  const getAccountStatus = (account) => {
    let status = null;
    if (!account.isActive) {
      status = (
        <Fragment>
          <Badge variant="error">Cancelled</Badge>
        </Fragment>
      );
    } else if (account.isSuspended) {
      status = (
        <Fragment>
          <Badge variant="warning">Suspended</Badge>
        </Fragment>
      );
    } else {
      status = (
        <Fragment>
          <Badge variant="success">Active</Badge>
        </Fragment>
      );
      status = account.isFixedTerm ? (
        <Fragment>
          {status}
          <Badge variant="default">Set to Term</Badge>
        </Fragment>
      ) : (
        <Fragment>
          {status}
          <Badge variant="default">Ongoing</Badge>
        </Fragment>
      );
    }
    if (account.overdueAmount > 0) {
      status = (
        <Fragment>
          {status}
          <Badge variant="error">Overdue</Badge>
        </Fragment>
      );
    }
    return status;
  };

  return (
    <InfoWidget
      title={'Customer Details'}
      actionButton={
        (permissions.includes('IsAdmin') ||
          permissions.includes('CanManageVariations')) && (
          <Link to={`/Customers/${adfitNo}/Variations`}>
            <TextButton variant={'primary'}>{'Edit'}</TextButton>
          </Link>
        )
      }
      isLoading={!customerDetails.adfitNo}
      contentLoader={<SkeletonLoader />}>
      <Styled.Container>
        <Styled.Title> {name || firstName}</Styled.Title>
        <Styled.AccountTagsContainer>
          {getAccountStatus(customerDetails)}
        </Styled.AccountTagsContainer>

        <RowContainer>
          <RowLeftText>{'Debitsucess ID'} </RowLeftText>
          <Styled.CopyButtonContainer>
            <RowRightText>{adfitNo}</RowRightText>
            <Styled.CopyButton
              id="clipboardAdfitNo"
              toolTipComponent={
                clipButtonState.clipboardAdfitNo
                  ? clipButtonState.clipboardAdfitNo
                  : 'Copy to Clipboard'
              }
              data-clipboard-text={adfitNo}>
              <CopyIcon />
            </Styled.CopyButton>
          </Styled.CopyButtonContainer>
        </RowContainer>

        <RowContainer>
          <RowLeftText>{'Business ID'} </RowLeftText>
          <Styled.CopyButtonContainer>
            <RowRightText>{businessId}</RowRightText>
            <Styled.CopyButton
              id="clipboardBusinessID"
              toolTipComponent={
                clipButtonState.clipboardBusinessID
                  ? clipButtonState.clipboardBusinessID
                  : 'Copy to Clipboard'
              }
              data-clipboard-text={businessId}>
              <CopyIcon />
            </Styled.CopyButton>
          </Styled.CopyButtonContainer>
        </RowContainer>

        <RowContainer>
          <RowLeftText>{'Business Name'} </RowLeftText>
          <RowRightText>{facilityName}</RowRightText>
        </RowContainer>

        {!isActive && (
          <RowContainer>
            <RowLeftText>{'Cancellation reason'} </RowLeftText>
            <RowRightText>
              {(customCancelReason &&
                'Facility request -' + customCancelReason) ||
                cancelReason}
            </RowRightText>
          </RowContainer>
        )}

        <Styled.Divider />

        <RowContainer>
          <RowLeftText>{'Full name'}</RowLeftText>
          <RowRightText>{fullName}</RowRightText>
        </RowContainer>

        <RowContainer>
          <RowLeftText>{'Date of birth'}</RowLeftText>
          <RowRightText>{dobStr}</RowRightText>
        </RowContainer>

        <RowContainer>
          <RowLeftText>{'Contact'} </RowLeftText>
          {preferredContact ? (
            <Styled.RowRightTextButton onClick={toggleExpandContact}>
              <span>{renderContactByType(preferredContact)}</span>
              {expandContact ? (
                <Styled.ExpandLessIcon />
              ) : (
                <Styled.ExpandMoreIcon />
              )}
            </Styled.RowRightTextButton>
          ) : (
            <RowRightText>{'-'}</RowRightText>
          )}
        </RowContainer>

        <Styled.Collapsible in={expandContact} timeout="auto" unmountOnExit>
          {filteredContacts.map((contact, index) => (
            <RowContainer
              key={index}
              last={index === filteredContacts.length - 1}>
              <RowLeftText>
                {getContactDisplayType(contact.contactType)}
              </RowLeftText>
              <RowRightText>{renderContactByType(contact)}</RowRightText>
            </RowContainer>
          ))}
        </Styled.Collapsible>

        <RowContainer>
          <RowLeftText>{'Address'} </RowLeftText>
          {preferredAddress ? (
            <Styled.RowRightTextButton onClick={toggleExpandAddress}>
              <span>{preferredAddress.address}</span>
              {isNotEmptyArray(addresses) ? (
                expandAddress ? (
                  <Styled.ExpandLessIcon />
                ) : (
                  <Styled.ExpandMoreIcon />
                )
              ) : null}
            </Styled.RowRightTextButton>
          ) : (
            <RowRightText>{'-'}</RowRightText>
          )}
        </RowContainer>

        <Styled.Collapsible in={expandAddress} timeout="auto" unmountOnExit>
          {filteredAddress.map((adrs) => {
            return (
              <RowContainer key={adrs.addressId}>
                <RowLeftText>
                  {' '}
                  {localisation.prompts.addressType[adrs.addressType]}{' '}
                </RowLeftText>
                <RowRightText>{adrs.address} </RowRightText>
              </RowContainer>
            );
          })}

          {previousAddress.length > 0 && <Styled.Divider />}
          {previousAddress.map((adrs, index) => {
            return (
              <RowContainer
                key={adrs.addressId}
                last={index === previousAddress.length - 1}>
                <RowLeftText>
                  {localisation.prompts.addressType[adrs.addressType]}{' '}
                </RowLeftText>
                <RowRightText>{adrs.address} </RowRightText>
              </RowContainer>
            );
          })}
        </Styled.Collapsible>
      </Styled.Container>
    </InfoWidget>
  );
};

PersonalInformationPreview.propTypes = {
  localisation: PropTypes.object,
  permissions: PropTypes.array.isRequired,
  customerDetails: PropTypes.object,
};

PersonalInformationPreview.defaultProps = {
  customerDetails: {},
};

export default PersonalInformationPreview;
